import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PhotoPicker } from "./shared/PhotoPicker";
import { PhotoThumb } from "./shared/PhotoThumb";
import { MachineSelect } from "./shared/MachineSelect";
import { FilterBar } from "./shared/FilterBar";
import { useMyAssets } from "./shared/useMyAssets";
import { NoAccess } from "./shared/NoAccess";
import { formatDateDDMMYYYY } from "../dailyLogCheck/BackfillDialog";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationBar } from "@/components/shared/PaginationBar";

const VIEW_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const DELETE_ROLES = ["Director", "Manager", "Back Office"];
const REMARKS_EDIT_ROLES = ["Director", "Manager", "Back Office"];

function statusBadge(entry) {
  if (entry.verified) return { label: "Verified", className: "bg-emerald-600" };
  if (entry.hours_run_out_of_range) return { label: "Flagged — Hours Run out of range", className: "bg-red-600" };
  return { label: "Unverified", className: "bg-slate-400" };
}

function SubmitForm() {
  const { assets, needsPicker } = useMyAssets();
  const [machineAssetId, setMachineAssetId] = useState("");
  const [reading, setReading] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photos, setPhotos] = useState([]);
  const [previousReading, setPreviousReading] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (needsPicker && !machineAssetId) {
      setPreviousReading(null);
      return;
    }
    const params = machineAssetId ? { machine_asset_id: machineAssetId } : {};
    client
      .get("/fleetcare/daily-log/previous-reading", { params })
      .then((res) => setPreviousReading(res.data.previous_reading))
      .catch(() => {});
  }, [machineAssetId, needsPicker]);

  const hoursRun = previousReading != null && reading ? parseFloat(reading) - parseFloat(previousReading) : null;
  const remarksMandatory = hoursRun !== null && !Number.isNaN(hoursRun) && hoursRun < 2;

  const submit = async (e) => {
    e.preventDefault();
    if (needsPicker && !machineAssetId) return toast.error("Please select which machine this entry is for.");
    setSubmitting(true);
    const fd = new FormData();
    fd.append("current_reading", reading);
    if (remarks) fd.append("remarks", remarks);
    if (machineAssetId) fd.append("machine_asset_id", machineAssetId);
    photos.forEach((f) => fd.append("photos", f));
    try {
      await client.post("/fleetcare/daily-log", fd);
      toast.success("Daily Log entry submitted");
      setReading("");
      setRemarks("");
      setPhotos([]);
      setPreviousReading(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="daily-log-submit-form">
      {needsPicker && <MachineSelect assets={assets} value={machineAssetId} onChange={setMachineAssetId} testId="daily-log-machine-select" />}
      {previousReading != null && (
        <p className="text-xs text-slate-500">
          Previous Reading: <span className="font-mono">{previousReading}</span>
        </p>
      )}
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">
          Current Reading <span className="text-red-500">*</span>
        </p>
        <Input data-testid="daily-log-reading-input" value={reading} onChange={(e) => setReading(e.target.value)} placeholder="HMR reading" required />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-700 mb-1.5">
          Remarks {remarksMandatory && <span className="text-red-500">* (machine ran under 2 hrs)</span>}
        </p>
        <Textarea data-testid="daily-log-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>
      <PhotoPicker label="Display Photo" required files={photos} onChange={setPhotos} testId="daily-log-photo-picker" />
      <Button type="submit" data-testid="daily-log-submit-button" disabled={submitting} className="bg-amber-600 hover:bg-amber-700">
        {submitting ? "Submitting..." : "Submit Daily Log"}
      </Button>
    </form>
  );
}

export function VerifyPanel({ entry, onClose, onUpdated, canDelete, canEditRemarks }) {
  const [verifiedReading, setVerifiedReading] = useState(entry.verified_reading || entry.current_reading);
  const [remarks, setRemarks] = useState(entry.remarks || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const badge = statusBadge(entry);

  const verify = async (verified) => {
    setBusy(true);
    try {
      const payload = { verified, verified_reading: verifiedReading };
      if (canEditRemarks) payload.remarks = remarks;
      const res = await client.put(`/fleetcare/daily-log/${entry.id}/verify`, payload);
      toast.success(verified ? "Marked as Verified" : "Marked as Unverified");
      onUpdated(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!reason.trim()) return toast.error("A reason is required to delete this entry");
    setBusy(true);
    try {
      await client.post(`/fleetcare/daily-log/${entry.id}/delete`, { reason });
      toast.success("Entry deleted");
      onClose();
      onUpdated(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="daily-log-detail-panel">
        <SheetHeader>
          <SheetTitle>{entry.asset_label}</SheetTitle>
          <SheetDescription>
            {entry.employee_name} · {entry.shift} · {formatDateDDMMYYYY(entry.effective_date)}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2 flex-wrap">
            {(entry.photos || []).map((p, i) => (
              <PhotoThumb key={i} path={`/fleetcare/daily-log/${entry.id}/photos/${i}`} alt={p.filename} testId={`daily-log-photo-${i}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Badge className={badge.className}>{badge.label}</Badge>
            <Badge variant="outline">AI: {entry.ai_verification_status}</Badge>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-mono">Current Reading</p>
            <p className="text-sm font-medium">{entry.current_reading}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Verified/Corrected Reading</p>
            <Input data-testid="daily-log-verified-reading-input" value={verifiedReading} onChange={(e) => setVerifiedReading(e.target.value)} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
            {canEditRemarks ? (
              <Textarea data-testid="daily-log-verify-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            ) : (
              <p data-testid="daily-log-remarks-readonly" className="text-sm text-slate-600 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 min-h-[2.5rem] whitespace-pre-wrap">
                {entry.remarks || "—"}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button data-testid="daily-log-mark-verified-button" disabled={busy} onClick={() => verify(true)} className="bg-emerald-600 hover:bg-emerald-700">
              Mark as Verified
            </Button>
            <Button data-testid="daily-log-mark-unverified-button" disabled={busy} variant="outline" onClick={() => verify(false)}>
              Mark as Unverified
            </Button>
          </div>
          {canDelete && (
            <div className="border-t border-slate-200 pt-4 space-y-2">
              <p className="text-sm font-medium text-red-600">Delete Entry (permanent, no undo)</p>
              <Textarea
                data-testid="daily-log-delete-reason-input"
                placeholder="Reason for deletion (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button data-testid="daily-log-delete-button" disabled={busy} variant="destructive" onClick={del}>
                Delete Entry
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ViewList({ role }) {
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", assetId: "" });
  const params = {};
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.assetId) params.asset_id = filters.assetId;
  const { items: entries, setItems: setEntries, loading, page, setPage, totalPages, reload } = usePaginatedList("/fleetcare/daily-log", params);
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-4" data-testid="daily-log-view">
      <FilterBar {...filters} onChange={(v) => setFilters((f) => ({ ...f, ...v }))} testIdPrefix="daily-log" />
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="daily-log-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Shift</TableHead>
              <TableHead>Machine</TableHead>
              <TableHead>Operator</TableHead>
              <TableHead>Reading</TableHead>
              <TableHead>Remarks</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>AI Check</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow key={e.id} data-testid={`daily-log-row-${e.id}`} className="cursor-pointer" onClick={() => setSelected(e)}>
                  <TableCell>{formatDateDDMMYYYY(e.effective_date)}</TableCell>
                  <TableCell>{e.shift}</TableCell>
                  <TableCell className="font-mono text-xs">{e.asset_label}</TableCell>
                  <TableCell>{e.employee_name}</TableCell>
                  <TableCell className="font-mono">{e.current_reading}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm text-slate-600" title={e.remarks || ""}>
                    {e.remarks || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadge(e).className}>{statusBadge(e).label}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{e.ai_verification_status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} testId="daily-log-pagination" />
      {selected && (
        <VerifyPanel
          entry={selected}
          canDelete={DELETE_ROLES.includes(role)}
          canEditRemarks={REMARKS_EDIT_ROLES.includes(role)}
          onClose={() => setSelected(null)}
          onUpdated={(updated) => {
            if (!updated) {
              reload();
              return;
            }
            setEntries((es) => es.map((x) => (x.id === updated.id ? updated : x)));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}

export default function DailyLogTab() {
  const { user } = useAuth();
  if (user?.role === "Operator") return <SubmitForm />;
  if (VIEW_ROLES.includes(user?.role)) return <ViewList role={user.role} />;
  return <NoAccess />;
}
